import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SuccessCoachingNoteControls } from '../components/SuccessCoachingNoteControls';
import { ManagedSummaryPanel } from '../components/ManagedSummaryPanel';
import { FlagForCorrectionDialog } from '../components/caseload-flags/FlagForCorrectionDialog';
import { flagApi, ticketPath, FLAG_UPDATED_EVENT, FLAG_MANAGEMENT_ROLES, FLAG_STATUS_STYLES } from '../components/caseload-flags/flagUtils';
import { SC_NOTE_SUBJECT, fillSuccessCoachingContent, unsuccessfulAttemptSummary, fetchSuccessCoachingWeek, sendSuccessCoachingMessages } from '../utils/successCoaching';
import { filterCaseNoteTemplates } from '../utils/caseNoteTemplates';
import { 
  ClipboardCheck, 
  Calendar, 
  Users, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  UserCheck,
  UserX,
  UserMinus,
  GraduationCap,
  Building,
  Phone,
  MessageSquare,
  Mail,
  MoreHorizontal,
  Plus,
  Send,
  Loader2,
  Sparkles,
  RotateCcw,
  Flag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SC_DEFAULT_TEMPLATE_NAME = 'The HELP Center: Success Coaching Encounter';

const CaseloadReviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [caseManagers, setCaseManagers] = useState([]);
  const [selectedCaseManager, setSelectedCaseManager] = useState('');
  const [header, setHeader] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingSnapshot, setGeneratingSnapshot] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [reopeningReview, setReopeningReview] = useState(false);
  
  // Phase 2 State
  const [progress, setProgress] = useState(null);
  const [caseloadMetrics, setCaseloadMetrics] = useState(null);
  const [participantMetrics, setParticipantMetrics] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({
    contact_outcomes: [],
    current_statuses: [],
    timesheet_options: [],
    certificate_upload_options: [],
    case_management_types: [],
    exit_outcomes: [],
    abawd_statuses: ['ABAWD', 'Non-ABAWD']
  });
  const [savingRecords, setSavingRecords] = useState({});
  const [flagsByRecord, setFlagsByRecord] = useState({});
  const [flagRecord, setFlagRecord] = useState(null);
  const canFlag = FLAG_MANAGEMENT_ROLES.includes(user?.role);
  
  // Phase 3 State - Communications & Case Notes
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'sms', 'email', 'phone', 'case_note'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [participantInfo, setParticipantInfo] = useState(null);
  const [loadingParticipantInfo, setLoadingParticipantInfo] = useState(false);
  const [sendingAction, setSendingAction] = useState(false);
  
  // Communication form state
  const [smsMessage, setSmsMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [phoneNotes, setPhoneNotes] = useState('');
  const [phoneCallerId, setPhoneCallerId] = useState('');
  const [outboundNumbers, setOutboundNumbers] = useState([]);
  const [initiatingCall, setInitiatingCall] = useState(false);
  
  // Case Note form state
  const [noteSubject, setNoteSubject] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteDateOfService, setNoteDateOfService] = useState(new Date().toISOString().split('T')[0]);
  const [caseNoteTemplates, setCaseNoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  
  // Summary view state
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [viewingSummaryRecord, setViewingSummaryRecord] = useState(null);
  
  // Success Coaching Summary state
  const [successCoachingSummaryDialogOpen, setSuccessCoachingSummaryDialogOpen] = useState(false);
  const [viewingSuccessCoachingRecord, setViewingSuccessCoachingRecord] = useState(null);
  const [successCoachingActionType, setSuccessCoachingActionType] = useState(null); // 'phone', 'sms', 'email', 'case_note'
  // Success Coaching weekly program on SC case notes
  const [scWeekInfo, setScWeekInfo] = useState(null);
  const [scUnsuccessful, setScUnsuccessful] = useState(false);
  const [scAnswered, setScAnswered] = useState(false);
  const [scWinWall, setScWinWall] = useState('');
  const [scBeforeToggle, setScBeforeToggle] = useState(null);

  // Return all active case note templates (same as participant profile Add Case Note)
  const getFilteredTemplates = () => filterCaseNoteTemplates(caseNoteTemplates, participantInfo?.case);

  // Success Coaching eligibility check: SNAP E&T enrollments on/after Oct 1, 2026
  const SUCCESS_COACHING_START_DATE = '2026-10-01';
  
  const isSuccessCoachingEligible = (record) => {
    // Must be SNAP E&T program - check both program_type format (snap_et) and program_name format (snap e&t)
    const programLower = selectedProgram?.toLowerCase() || '';
    const isSnapEt = programLower.includes('snap') && (programLower.includes('e&t') || programLower.includes('et') || programLower.includes('_et'));
    if (!isSnapEt) {
      return false;
    }
    // Check enrollment date is on or after Oct 1, 2026
    const enrollmentDate = record.enrollment_start_date;
    if (!enrollmentDate) return false;
    return enrollmentDate >= SUCCESS_COACHING_START_DATE;
  };
  
  const isSuccessCoachingProgram = () => {
    const programLower = selectedProgram?.toLowerCase() || '';
    // Match snap_et_*, SNAP E&T, etc.
    return programLower.includes('snap') && (programLower.includes('e&t') || programLower.includes('et') || programLower.includes('_et'));
  };
  
  // Check if current user is the assigned case manager for this caseload review
  const isCurrentUserAssignedCaseManager = () => {
    return user?.id === selectedCaseManager;
  };
  
  // View Success Coaching Summary
  const viewSuccessCoachingSummary = (record) => {
    setViewingSuccessCoachingRecord(record);
    setSuccessCoachingSummaryDialogOpen(true);
  };
  
  // Open Success Coaching action with case manager restriction check
  const openSuccessCoachingAction = async (record, type) => {
    // Check if current user is the assigned case manager
    if (isCurrentUserAssignedCaseManager()) {
      toast.error('Action not allowed: The assigned case manager cannot trigger Success Coaching automation.');
      return;
    }
    
    // Use the regular action dialog but track it's for success coaching
    setSuccessCoachingActionType(type);
    openActionDialog(record, type, type === 'case_note' ? SC_DEFAULT_TEMPLATE_NAME : null);
  };
  
  // Handle Success Coaching case note (same as regular but updates success_coaching_summary)
  const handleAddSuccessCoachingCaseNote = async () => {
    if (!noteSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!noteContent.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    // Check case manager restriction
    if (isCurrentUserAssignedCaseManager()) {
      toast.error('Action not allowed: The assigned case manager cannot trigger Success Coaching automation.');
      return;
    }
    
    setSendingAction(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/caseload-review/records/${selectedRecord.id}/success-coaching-note`,
        { 
          subject: noteSubject,
          content: noteContent,
          date_of_service: noteDateOfService,
          note_type: 'success_coaching',
          template_id: selectedTemplate || undefined,
          unsuccessful: scUnsuccessful
        }
      );
      
      toast.success('Success Coaching note added and AI summary generated');
      if (scWeekInfo && !scWeekInfo.completed) {
        await sendSuccessCoachingMessages(participantInfo?.case?.id || selectedRecord.case_id, scWeekInfo.week_number, response.data.note_id, scUnsuccessful ? '' : scWinWall, scUnsuccessful);
      }
      
      // Update local record with Success Coaching AI summary
      setRecords(prev => prev.map(r => 
        r.id === selectedRecord.id 
          ? { 
              ...r, 
              success_coaching_summary: response.data.success_coaching_summary,
              success_coaching_summary_note_id: response.data.note_id,
              success_coaching_summary_note_subject: noteSubject,
              success_coaching_summary_generated_at: response.data.created_at
            }
          : r
      ));
      
      setSuccessCoachingActionType(null);
      closeActionDialog();
    } catch (error) {
      console.error('Error adding success coaching note:', error);
      toast.error(error.response?.data?.detail || 'Failed to add Success Coaching note');
    } finally {
      setSendingAction(false);
    }
  };

  // Refs for sticky horizontal scrollbar
  const tableScrollRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const tableRef = useRef(null);
  const scUnsuccessfulRef = useRef(false);
  const [tableWidth, setTableWidth] = useState(3000);

  // Sync scroll positions and table width
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.scrollWidth);
      }
    };
    
    updateTableWidth();
    window.addEventListener('resize', updateTableWidth);
    
    // Update after records load
    const timer = setTimeout(updateTableWidth, 100);
    
    return () => {
      window.removeEventListener('resize', updateTableWidth);
      clearTimeout(timer);
    };
  }, [records]);

  // Fetch available weeks
  const fetchWeeks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/caseload-review/weeks`);
      setWeeks(response.data.weeks || []);
      if (response.data.weeks && response.data.weeks.length > 0) {
        setSelectedWeek(response.data.weeks[0]);
      }
    } catch (error) {
      console.error('Error fetching weeks:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load available review weeks');
      }
    }
  };

  // Fetch programs for selected week
  const fetchPrograms = async (weekOf) => {
    try {
      const response = await axios.get(`${API_URL}/api/caseload-review/programs?week_of=${weekOf}`);
      setPrograms(response.data.programs || []);
      if (response.data.programs && response.data.programs.length === 1) {
        setSelectedProgram(response.data.programs[0].program_type);
      } else {
        setSelectedProgram('');
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  // Fetch case managers for selected week and program
  const fetchCaseManagers = async (weekOf, programType) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/caseload-review/case-managers?week_of=${weekOf}&program_type=${programType}`
      );
      setCaseManagers(response.data.case_managers || []);
      if (response.data.case_managers && response.data.case_managers.length === 1) {
        setSelectedCaseManager(response.data.case_managers[0].case_manager_id);
      } else {
        setSelectedCaseManager('');
      }
    } catch (error) {
      console.error('Error fetching case managers:', error);
    }
  };

  // Fetch caseload review records with metrics (Phase 2 endpoint)
  const fetchRecordsWithMetrics = useCallback(async () => {
    if (!selectedWeek || !selectedProgram || !selectedCaseManager) {
      setHeader(null);
      setRecords([]);
      setProgress(null);
      setCaseloadMetrics(null);
      setParticipantMetrics(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/caseload-review/records-with-metrics?week_of=${selectedWeek}&program_type=${selectedProgram}&case_manager_id=${selectedCaseManager}`
      );
      setHeader(response.data.header);
      const fetched = response.data.records || [];
      if (isSuccessCoachingProgram()) {
        const key = (r) => `${r.last_name || ''} ${r.first_name || ''}`.trim().toLowerCase();
        fetched.sort((a, b) => key(a).localeCompare(key(b)));
      }
      setRecords(fetched);
      setProgress(response.data.progress);
      setCaseloadMetrics(response.data.caseload_metrics);
      setParticipantMetrics(response.data.participant_metrics);
      if (response.data.dropdown_options) {
        setDropdownOptions(response.data.dropdown_options);
      }
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load caseload review data');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, selectedProgram, selectedCaseManager]);

  // Autosave record field
  const updateRecordField = async (recordId, field, value) => {
    setSavingRecords(prev => ({ ...prev, [recordId]: true }));
    
    try {
      const response = await axios.put(
        `${API_URL}/api/caseload-review/records/${recordId}`,
        { [field]: value }
      );
      
      // Update local record state
      setRecords(prev => prev.map(r => 
        r.id === recordId 
          ? { ...r, [field]: value, review_completed: response.data.review_completed }
          : r
      ));
      
      // Recalculate progress locally
      const updatedRecords = records.map(r => 
        r.id === recordId ? { ...r, [field]: value } : r
      );
      updateLocalMetrics(updatedRecords);
      if (flagsByRecord[recordId]) fetchFlags();
      
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingRecords(prev => ({ ...prev, [recordId]: false }));
    }
  };

  // Update local metrics after a change
  const updateLocalMetrics = (updatedRecords) => {
    // Count completed reviews (including State ID requirement)
    const requiredFields = ['contact_outcome', 'current_status', 'timesheet', 'case_management_type', 'exit_outcome'];
    const completed = updatedRecords.filter(r => {
      const hasStateId = r.state_id_number && r.state_id_number.trim() !== '';
      return hasStateId && requiredFields.every(f => r[f] && r[f] !== '');
    }).length;
    
    setProgress(prev => prev ? {
      ...prev,
      completed_reviews: completed,
      remaining_reviews: prev.total_participants - completed,
      progress_percentage: prev.total_participants > 0 ? Math.round((completed / prev.total_participants) * 100 * 10) / 10 : 0,
      can_mark_complete: completed === prev.total_participants && prev.total_participants > 0
    } : null);
    
    // Update participant metrics based on current_status selections
    const active = updatedRecords.filter(r => r.current_status === 'Active').length;
    const inactive = updatedRecords.filter(r => r.current_status === 'Inactive').length;
    const pendingStart = updatedRecords.filter(r => r.current_status === 'Pending Start').length;
    const exited = updatedRecords.filter(r => r.current_status === 'Exited').length;
    
    setParticipantMetrics(prev => prev ? {
      ...prev,
      total_active: active,
      total_inactive: inactive,
      total_pending_start: pendingStart,
      total_exited_this_week: exited
    } : null);
  };

  // Generate snapshot manually
  const generateSnapshot = async () => {
    setGeneratingSnapshot(true);
    try {
      const response = await axios.post(`${API_URL}/api/caseload-review/generate-snapshot`, {});
      toast.success(`Snapshot generated: ${response.data.headers_created} headers, ${response.data.records_created} records`);
      await fetchWeeks();
    } catch (error) {
      console.error('Error generating snapshot:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate snapshot');
    } finally {
      setGeneratingSnapshot(false);
    }
  };

  // Mark review as complete
  const markReviewComplete = async () => {
    if (!header?.id) return;
    
    setMarkingComplete(true);
    try {
      const response = await axios.post(`${API_URL}/api/caseload-review/headers/${header.id}/complete`);
      toast.success('Caseload review marked as Completed!');
      setHeader(prev => ({ ...prev, review_status: 'Completed', completed_at: response.data.completed_at }));
      setProgress(prev => prev ? { ...prev, can_mark_complete: false } : null);
    } catch (error) {
      console.error('Error marking complete:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark review as complete');
    } finally {
      setMarkingComplete(false);
    }
  };

  // Reopen review for editing
  const reopenReview = async () => {
    if (!header?.id) return;
    
    setReopeningReview(true);
    try {
      const response = await axios.post(`${API_URL}/api/caseload-review/headers/${header.id}/reopen`);
      toast.success('Caseload review reopened for editing');
      setHeader(prev => ({ ...prev, review_status: 'In Progress', reopened_at: response.data.reopened_at }));
      // Re-calculate progress to allow marking complete again
      if (progress) {
        setProgress(prev => ({ ...prev, can_mark_complete: prev.completed === prev.total && prev.total > 0 }));
      }
    } catch (error) {
      console.error('Error reopening review:', error);
      toast.error(error.response?.data?.detail || 'Failed to reopen review');
    } finally {
      setReopeningReview(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MM/dd/yyyy');
    } catch {
      return dateStr;
    }
  };

  // Format week display
  const formatWeekDisplay = (weekOf) => {
    if (!weekOf) return '';
    try {
      return `Week of ${format(parseISO(weekOf), 'MMMM d, yyyy')}`;
    } catch {
      return `Week of ${weekOf}`;
    }
  };

  // Get program display name
  const getProgramDisplayName = () => {
    const program = programs.find(p => p.program_type === selectedProgram);
    if (program) {
      return `${program.program_name}: Caseload Review`;
    }
    return 'Caseload Review';
  };

  // Format barriers for display
  const formatBarriers = (barriers) => {
    if (!barriers || barriers.length === 0) return '-';
    if (typeof barriers === 'string') return barriers;
    return barriers.join(', ');
  };

  // Check if a record is complete
  const isRecordComplete = (record) => {
    const requiredFields = ['contact_outcome', 'current_status', 'timesheet', 'case_management_type', 'exit_outcome'];
    // State ID Number is also required for completion
    const hasStateId = record.state_id_number && record.state_id_number.trim() !== '';
    const hasRequiredFields = requiredFields.every(f => record[f] && record[f] !== '');
    
    // Targeted Outcomes fields are also required for completion
    const hasAbawdStatus = record.abawd_status && record.abawd_status.trim() !== '' && record.abawd_status !== '-';
    const hasCertPeriod = (record.certification_period && record.certification_period.trim() !== '' && record.certification_period !== '-') ||
                         (record.certification_start_date && record.certification_start_date.trim() !== '');
    const hasEmploymentGoal = record.employment_goal && record.employment_goal.trim() !== '' && record.employment_goal !== '-';
    // Barriers can be empty array or "None", but should not be undefined or "-"
    const hasBarriers = record.barriers !== undefined || 
                       (record.barriers_str && record.barriers_str.trim() !== '' && record.barriers_str !== '-');
    
    return hasStateId && hasRequiredFields && hasAbawdStatus && hasCertPeriod && hasEmploymentGoal && hasBarriers;
  };

  // Check if State ID is missing for a record
  const isStateIdMissing = (record) => {
    return !record.state_id_number || record.state_id_number.trim() === '';
  };

  // Check if targeted outcomes fields are missing
  const isAbawdMissing = (record) => {
    return !record.abawd_status || record.abawd_status.trim() === '' || record.abawd_status === '-';
  };
  
  const isCertPeriodMissing = (record) => {
    const hasCertPeriod = record.certification_period && record.certification_period.trim() !== '' && record.certification_period !== '-';
    const hasCertStart = record.certification_start_date && record.certification_start_date.trim() !== '';
    return !hasCertPeriod && !hasCertStart;
  };
  
  const isEmploymentGoalMissing = (record) => {
    return !record.employment_goal || record.employment_goal.trim() === '' || record.employment_goal === '-';
  };
  
  const isBarriersMissing = (record) => {
    if (record.barriers !== undefined && record.barriers !== null) return false;
    return !record.barriers_str || record.barriers_str.trim() === '' || record.barriers_str === '-';
  };
  
  // Get list of missing fields for a record
  const getMissingFields = (record) => {
    const missing = [];
    if (isStateIdMissing(record)) missing.push('State ID');
    if (isAbawdMissing(record)) missing.push('ABAWD Status');
    if (isCertPeriodMissing(record)) missing.push('Cert Period');
    if (isEmploymentGoalMissing(record)) missing.push('Employment Goal');
    if (isBarriersMissing(record)) missing.push('Barriers');
    const requiredFields = ['contact_outcome', 'current_status', 'timesheet', 'case_management_type', 'exit_outcome'];
    requiredFields.forEach(f => {
      if (!record[f] || record[f] === '') {
        const label = f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        missing.push(label);
      }
    });
    return missing;
  };

  // Update Targeted Outcomes field - saves directly to case's targeted_outcomes_data
  const updateTargetedOutcomesField = async (recordId, field, value) => {
    const key = `${recordId}-${field}`;
    setSavingTargetedOutcomes(prev => ({ ...prev, [key]: true }));
    
    try {
      const payload = {};
      
      // Handle certification period dates
      if (field === 'certification_start_date' || field === 'certification_end_date') {
        payload[field] = value;
      } else if (field === 'barriers') {
        // Convert comma-separated string to array
        const barriersArray = value ? value.split(',').map(b => b.trim()).filter(b => b) : [];
        payload.barriers = barriersArray;
      } else {
        payload[field] = value;
      }
      
      const response = await axios.put(
        `${API_URL}/api/caseload-review/records/${recordId}/targeted-outcomes`,
        payload
      );
      
      // Update local record state
      setRecords(prev => prev.map(r => {
        if (r.id === recordId) {
          const updated = { ...r };
          if (field === 'certification_start_date' || field === 'certification_end_date') {
            updated[field] = value;
            // Also update the display period
            const start = field === 'certification_start_date' ? value : r.certification_start_date || '';
            const end = field === 'certification_end_date' ? value : r.certification_end_date || '';
            if (start && end) {
              updated.certification_period = `${start} to ${end}`;
            } else if (start) {
              updated.certification_period = `${start} to -`;
            }
          } else if (field === 'barriers') {
            const barriersArray = value ? value.split(',').map(b => b.trim()).filter(b => b) : [];
            updated.barriers = barriersArray;
            updated.barriers_str = barriersArray.length > 0 ? barriersArray.join(', ') : 'None';
          } else {
            updated[field] = value;
          }
          updated.review_completed = response.data.review_completed;
          return updated;
        }
        return r;
      }));
      
      // Clear editing state for this field
      setEditingTargetedOutcomes(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      
      // Recalculate progress locally
      const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      toast.success(`${fieldLabel} saved to Targeted Outcomes`);
      if (flagsByRecord[recordId]) fetchFlags();
    } catch (error) {
      console.error('Error updating targeted outcomes:', error);
      toast.error(error.response?.data?.detail || 'Failed to save field');
    } finally {
      setSavingTargetedOutcomes(prev => ({ ...prev, [key]: false }));
    }
  };

  // State for inline State ID editing
  const [editingStateId, setEditingStateId] = useState({});
  const [savingStateId, setSavingStateId] = useState({});

  // State for inline Targeted Outcomes editing
  const [editingTargetedOutcomes, setEditingTargetedOutcomes] = useState({});
  const [savingTargetedOutcomes, setSavingTargetedOutcomes] = useState({});
  const [targetedOutcomesRequiredFields, setTargetedOutcomesRequiredFields] = useState([]);

  // Update State ID Number - saves directly to participant profile
  const updateStateIdNumber = async (recordId, participantId, value) => {
    if (!value || !value.trim()) {
      toast.error('State ID Number is required');
      return;
    }
    
    setSavingStateId(prev => ({ ...prev, [recordId]: true }));
    
    try {
      const response = await axios.put(
        `${API_URL}/api/caseload-review/records/${recordId}/state-id`,
        { state_id_number: value.trim() }
      );
      
      // Update local record state
      setRecords(prev => prev.map(r => 
        r.id === recordId 
          ? { ...r, state_id_number: value.trim(), review_completed: response.data.review_completed }
          : r
      ));
      
      // Clear editing state
      setEditingStateId(prev => ({ ...prev, [recordId]: undefined }));
      
      // Recalculate progress locally
      const updatedRecords = records.map(r => 
        r.id === recordId ? { ...r, state_id_number: value.trim() } : r
      );
      updateLocalMetrics(updatedRecords);
      
      toast.success('State ID Number saved to participant profile');
      if (flagsByRecord[recordId]) fetchFlags();
    } catch (error) {
      console.error('Error updating State ID:', error);
      toast.error(error.response?.data?.detail || 'Failed to save State ID Number');
    } finally {
      setSavingStateId(prev => ({ ...prev, [recordId]: false }));
    }
  };

  // Effects
  useEffect(() => {
    fetchWeeks();
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      fetchPrograms(selectedWeek);
    } else {
      setPrograms([]);
      setSelectedProgram('');
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (selectedWeek && selectedProgram) {
      fetchCaseManagers(selectedWeek, selectedProgram);
    } else {
      setCaseManagers([]);
      setSelectedCaseManager('');
    }
  }, [selectedWeek, selectedProgram]);

  useEffect(() => {
    fetchRecordsWithMetrics();
  }, [fetchRecordsWithMetrics]);

  // Latest correction flag per participant review in this header
  const fetchFlags = useCallback(async () => {
    if (!header?.id) { setFlagsByRecord({}); return; }
    try {
      const flags = await flagApi.forHeader(header.id);
      const map = {};
      flags.forEach((f) => { if (!map[f.record_id]) map[f.record_id] = f; });
      setFlagsByRecord(map);
    } catch (error) {
      console.error('Error fetching review flags:', error);
    }
  }, [header?.id]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  useEffect(() => {
    const onFlagUpdated = () => { fetchFlags(); fetchRecordsWithMetrics(); };
    window.addEventListener(FLAG_UPDATED_EVENT, onFlagUpdated);
    return () => window.removeEventListener(FLAG_UPDATED_EVENT, onFlagUpdated);
  }, [fetchFlags, fetchRecordsWithMetrics]);

  // Navigate to previous/next week
  const navigateWeek = (direction) => {
    const currentIndex = weeks.indexOf(selectedWeek);
    if (direction === 'prev' && currentIndex < weeks.length - 1) {
      setSelectedWeek(weeks[currentIndex + 1]);
    } else if (direction === 'next' && currentIndex > 0) {
      setSelectedWeek(weeks[currentIndex - 1]);
    }
  };

  // Dropdown component for inline editing
  const InlineSelect = ({ value, options, onChange, placeholder, disabled, recordId }) => {
    const isSaving = savingRecords[recordId];
    return (
      <Select 
        value={value || ''} 
        onValueChange={onChange}
        disabled={disabled || isSaving}
      >
        <SelectTrigger className={`h-8 text-xs min-w-[140px] ${isSaving ? 'opacity-50' : ''}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt} className="text-xs">
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // Phase 3 Functions - Communications & Case Notes
  const openActionDialog = async (record, type, defaultTemplateName = null) => {
    setSelectedRecord(record);
    setActionType(type);
    setLoadingParticipantInfo(true);
    setActionDialogOpen(true);
    
    // Reset form fields
    setSmsMessage('');
    setEmailSubject('');
    setEmailBody('');
    setPhoneNotes('');
    setPhoneCallerId('');
    setNoteSubject('');
    setNoteContent('');
    setNoteDateOfService(new Date().toISOString().split('T')[0]);
    setSelectedTemplate('');
    setScWeekInfo(null);
    setScUnsuccessful(false); scUnsuccessfulRef.current = false;
    setScAnswered(false);
    setScWinWall('');
    setScBeforeToggle(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/caseload-review/records/${record.id}/participant-info`);
      setParticipantInfo(response.data);
      
      // Fetch outbound numbers for phone calls
      if (type === 'phone') {
        try {
          const numbersRes = await axios.get(`${API_URL}/api/admin/outbound-phone-numbers`);
          const activeNumbers = (numbersRes.data.numbers || []).filter(n => n.is_active);
          setOutboundNumbers(activeNumbers);
          if (activeNumbers.length > 0) {
            setPhoneCallerId(activeNumbers[0].phone_number);
          }
        } catch (numErr) {
          console.error('Error fetching outbound numbers:', numErr);
        }
      }
      
      // Fetch case note templates
      if (type === 'case_note') {
        try {
          const templatesRes = await axios.get(`${API_URL}/api/case-note-templates`);
          // Sort templates alphabetically by name (A-Z)
          const sortedTemplates = [...(templatesRes.data || [])].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
          );
          setCaseNoteTemplates(sortedTemplates);
          let weekInfo = null;
          if (defaultTemplateName) {
            weekInfo = await fetchSuccessCoachingWeek(response.data?.case?.id || record.case_id);
            setScWeekInfo(weekInfo);
          }
          if (defaultTemplateName) {
            const wanted = defaultTemplateName.trim().toLowerCase();
            const defaultTemplate = sortedTemplates.find(t => (t.name || '').trim().toLowerCase() === wanted);
            if (defaultTemplate) {
              handleTemplateSelect(defaultTemplate.id, sortedTemplates, response.data, weekInfo);
            } else {
              toast.warning(`Template "${defaultTemplateName}" was not found. Please choose a template.`);
            }
          }
        } catch (tplErr) {
          console.error('Error fetching case note templates:', tplErr);
        }
      }
    } catch (error) {
      console.error('Error fetching participant info:', error);
      toast.error('Failed to load participant information');
    } finally {
      setLoadingParticipantInfo(false);
    }
  };

  const closeActionDialog = () => {
    setActionDialogOpen(false);
    setSelectedRecord(null);
    setActionType(null);
    setParticipantInfo(null);
    setSuccessCoachingActionType(null);
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingAction(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/caseload-review/records/${selectedRecord.id}/send-sms`,
        { message: smsMessage, is_free_form: true }
      );
      
      toast.success(response.data.is_mock ? 'SMS sent (preview mode)' : 'SMS sent successfully');
      
      // Update local record state with communication date
      setRecords(prev => prev.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, last_communication_date: response.data.communication_date, last_communication_type: 'sms' }
          : r
      ));
      
      closeActionDialog();
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingAction(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Please enter subject and message');
      return;
    }
    
    setSendingAction(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/caseload-review/records/${selectedRecord.id}/send-email`,
        { subject: emailSubject, body: emailBody }
      );
      
      toast.success(response.data.is_mock ? 'Email sent (preview mode)' : 'Email sent successfully');
      
      setRecords(prev => prev.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, last_communication_date: response.data.communication_date, last_communication_type: 'email' }
          : r
      ));
      
      closeActionDialog();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingAction(false);
    }
  };

  const handleInitiateCall = async () => {
    if (!participantInfo?.participant?.phone) {
      toast.error('Participant does not have a phone number');
      return;
    }
    
    setInitiatingCall(true);
    setSendingAction(true);
    try {
      // Use the existing RingOut endpoint from Communications
      const response = await axios.post(
        `${API_URL}/api/communications/call/initiate`,
        { 
          participant_id: participantInfo.participant.id,
          case_id: participantInfo.case?.id,
          phone_number: participantInfo.participant.phone,
          caller_id: phoneCallerId,
          from_number: phoneCallerId,
          call_type: 'outbound',
          notes: phoneNotes || 'Call initiated from Caseload Review'
        }
      );
      
      const isMock = response.data.is_mock || !response.data.success;
      if (isMock) {
        toast.success('Call initiated (Preview Mode - RingCentral not fully configured)');
      } else {
        toast.success('Call initiated! Your phone will ring first, then the participant will be connected.');
      }
      
      // Update local record state with communication date
      setRecords(prev => prev.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, last_communication_date: response.data.communication?.timestamp, last_communication_type: 'phone' }
          : r
      ));
      
      closeActionDialog();
    } catch (error) {
      console.error('Error initiating call:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate call';
      toast.error(errorMsg);
    } finally {
      setInitiatingCall(false);
      setSendingAction(false);
    }
  };

  // Fill template merge fields for the participant on this pop-up
  const fillTemplateBody = async (raw, participantInfo, weekInfo) => {
    const p = participantInfo.participant || {};
    const c = participantInfo.case || {};
    const NOT_ON_FILE = 'Not on file';
    let body = (raw || '').replace(/%7B%7B(?:\s|%20|\+)*([\w.]+)(?:\s|%20|\+)*%7D%7D/gi, '{{$1}}');
    
    // Replace common merge fields
    body = body.replace(/\{\{participant_full_name\}\}/g, `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'N/A');
    body = body.replace(/\{\{participant_first_name\}\}/g, p.first_name || 'N/A');
    body = body.replace(/\{\{participant_last_name\}\}/g, p.last_name || 'N/A');
    body = body.replace(/\{\{(participant_email|email_address)\}\}/g, (p.email || '').trim() || NOT_ON_FILE);
    body = body.replace(/\{\{(participant_phone|telephone)\}\}/g, (p.phone || '').trim() || NOT_ON_FILE);
    body = body.replace(/\{\{today_date\}\}/g, formatDateUtil(new Date()));
    body = body.replace(/\{\{program_name\}\}/g, c.program_name || selectedProgram || 'N/A');
    body = body.replace(/\{\{case_number\}\}/g, c.case_number || 'N/A');
    if (weekInfo && !weekInfo.completed) {
      body = body.replace(/\{\{success_coaching_week_topic\}\}/g, weekInfo.topic || '');
    }
    const caseId = c.id || selectedRecord?.case_id;
    if (caseId && /\{\{[^}]+\}\}/.test(body.replace(/\{\{(win_wall|personalized_encounter_summary)\}\}/g, ''))) {
      try {
        const res = await axios.post(`${API_URL}/api/case-note-templates/populate`, { case_id: caseId, body });
        body = res.data.populated_body ?? body;
      } catch (e) {
        console.error('Error filling template merge fields:', e);
      }
    }
    // Success Coaching placeholders are filled later from the weekly questions
    body = body.replace(/\{\{(?!win_wall\}\}|personalized_encounter_summary\}\})[^{}]+\}\}/g, NOT_ON_FILE);

    return body;
  };

  const handleTemplateSelect = async (templateId, templatesList = caseNoteTemplates, info = participantInfo, weekInfo = scWeekInfo) => {
    setSelectedTemplate(templateId);
    
    if (!templateId) {
      // Clear template content
      return;
    }
    
    const participantInfo = info;
    const template = templatesList.find(t => t.id === templateId);
    if (template) {
      // Set subject from template
      if (template.subject) {
        setNoteSubject(template.subject);
      } else if (template.name) {
        setNoteSubject(template.name);
      }
      
      // Set body from template, replacing merge fields if possible
      if (template.body && participantInfo) {
        setNoteContent(await fillTemplateBody(template.body, participantInfo, weekInfo));
      }
    }
  };

  const handleScToggleUnsuccessful = (on) => {
    scUnsuccessfulRef.current = on;
    setScUnsuccessful(on);
    if (on) {
      setScBeforeToggle({ subject: noteSubject, content: noteContent });
      setNoteSubject(SC_NOTE_SUBJECT);
      const tpl = scWeekInfo?.unsuccessful?.case_note_body || '';
      if (!tpl.trim()) { setNoteContent(''); return; }
      fillTemplateBody(tpl, participantInfo, scWeekInfo).then((body) => scUnsuccessfulRef.current && setNoteContent(fillSuccessCoachingContent(body, {
        topic: scWeekInfo?.topic, winWall: '', summary: unsuccessfulAttemptSummary(scWeekInfo, participantInfo?.participant?.first_name)
      })));
    } else if (scBeforeToggle) {
      setNoteSubject(scBeforeToggle.subject);
      setNoteContent(scBeforeToggle.content);
      setScBeforeToggle(null);
    }
  };

  const handleScQuestionsComplete = (result) => {
    setNoteContent(prev => fillSuccessCoachingContent(prev, {
      topic: result.week_topic, winWall: result.win_wall, summary: result.personalized_encounter_summary
    }));
    setScWinWall(result.win_wall || '');
    setScAnswered(true);
    toast.success('Personalized Encounter Summary added to the note');
  };

  const handleAddCaseNote = async () => {
    if (!noteSubject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!noteContent.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    setSendingAction(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/caseload-review/records/${selectedRecord.id}/case-note`,
        { 
          subject: noteSubject,
          content: noteContent,
          date_of_service: noteDateOfService,
          note_type: 'case_management',
          template_id: selectedTemplate || undefined
        }
      );
      
      toast.success('Case note added and AI summary generated');
      
      // Update local record with AI summary
      setRecords(prev => prev.map(r => 
        r.id === selectedRecord.id 
          ? { 
              ...r, 
              case_management_summary: response.data.case_management_summary,
              case_management_summary_note_id: response.data.note_id,
              case_management_summary_note_subject: noteSubject,
              case_management_summary_generated_at: response.data.created_at
            }
          : r
      ));
      
      closeActionDialog();
    } catch (error) {
      console.error('Error adding case note:', error);
      toast.error(error.response?.data?.detail || 'Failed to add case note');
    } finally {
      setSendingAction(false);
    }
  };

  const SUMMARY_ADMIN_ROLES = ['system_admin', 'executive', 'operations_manager'];
  const canManageSummary = (record) => !!record && (SUMMARY_ADMIN_ROLES.includes(user?.role) || record.assigned_user_id === user?.id);

  const applySummaryUpdate = (recordId, patch) => {
    setRecords(prev => prev.map(r => r.id === recordId ? { ...r, ...patch } : r));
    setViewingSummaryRecord(prev => prev?.id === recordId ? { ...prev, ...patch } : prev);
    setViewingSuccessCoachingRecord(prev => prev?.id === recordId ? { ...prev, ...patch } : prev);
  };

  const viewSummary = (record) => {
    setViewingSummaryRecord(record);
    setSummaryDialogOpen(true);
  };

  const getActionDialogTitle = () => {
    switch (actionType) {
      case 'sms': return 'Send Text Message';
      case 'email': return 'Send Email';
      case 'phone': return 'Make Phone Call';
      case 'case_note': return 'Add Case Note';
      default: return 'Action';
    }
  };

  const getActionDialogIcon = () => {
    switch (actionType) {
      case 'sms': return <MessageSquare className="w-5 h-5 text-green-500" />;
      case 'email': return <Mail className="w-5 h-5 text-blue-500" />;
      case 'phone': return <Phone className="w-5 h-5 text-purple-500" />;
      case 'case_note': return <FileText className="w-5 h-5 text-orange-500" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="caseload-review-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-primary" />
            {getProgramDisplayName()}
          </h1>
          <p className="text-muted-foreground mt-1">
            Review participant caseloads for SNAP E&T Programs
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={generateSnapshot}
          disabled={generatingSnapshot}
          className="gap-2"
          data-testid="generate-snapshot-btn"
        >
          <RefreshCw className={`w-4 h-4 ${generatingSnapshot ? 'animate-spin' : ''}`} />
          {generatingSnapshot ? 'Generating...' : 'Generate Snapshot'}
        </Button>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Review Period
          </CardTitle>
          <CardDescription>
            Select the week, program, and case manager to view
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Week Selector with Navigation */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Week</label>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => navigateWeek('prev')}
                  disabled={!selectedWeek || weeks.indexOf(selectedWeek) >= weeks.length - 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select week" />
                  </SelectTrigger>
                  <SelectContent>
                    {weeks.map((week) => (
                      <SelectItem key={week} value={week}>
                        {formatWeekDisplay(week)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => navigateWeek('next')}
                  disabled={!selectedWeek || weeks.indexOf(selectedWeek) <= 0}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Program Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Program</label>
              <Select 
                value={selectedProgram} 
                onValueChange={setSelectedProgram}
                disabled={!selectedWeek || programs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program) => (
                    <SelectItem key={program.program_type} value={program.program_type}>
                      {program.program_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Case Manager Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Case Manager</label>
              <Select 
                value={selectedCaseManager} 
                onValueChange={setSelectedCaseManager}
                disabled={!selectedProgram || caseManagers.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select case manager" />
                </SelectTrigger>
                <SelectContent>
                  {caseManagers.map((cm) => (
                    <SelectItem key={cm.case_manager_id} value={cm.case_manager_id}>
                      {cm.case_manager_name} ({cm.total_participants} participants)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Badge */}
            <div className="flex items-center gap-4">
              {header && (
                <Badge 
                  variant={
                    header.review_status === 'Completed' ? 'default' : 
                    header.review_status === 'In Progress' ? 'secondary' : 'outline'
                  }
                  className={
                    header.review_status === 'Completed' ? 'bg-green-500' :
                    header.review_status === 'In Progress' ? 'bg-blue-500 text-white' : ''
                  }
                >
                  {header.review_status === 'Completed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                  {header.review_status === 'In Progress' && <Clock className="w-3 h-3 mr-1" />}
                  {header.review_status}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Week Display Banner */}
      {selectedWeek && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary" />
            <span className="font-semibold text-lg">{formatWeekDisplay(selectedWeek)}</span>
          </div>
          {header && (
            <span className="text-sm text-muted-foreground">
              Snapshot generated: {formatDate(header.snapshot_generated_at?.split('T')[0])}
            </span>
          )}
        </div>
      )}

      {/* Progress & Metrics Section */}
      {progress && header && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Progress Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                Review Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <span className="text-3xl font-bold text-primary">{progress.completed_reviews}</span>
                <span className="text-lg text-muted-foreground"> of {progress.total_participants}</span>
                <p className="text-sm text-muted-foreground">Participants Reviewed</p>
              </div>
              <Progress value={progress.progress_percentage} className="h-2" />
              <p className="text-center text-sm font-medium">{progress.progress_percentage}% Complete</p>
              
              {progress.can_mark_complete && header.review_status !== 'Completed' && (
                <Button 
                  className="w-full gap-2" 
                  onClick={markReviewComplete}
                  disabled={markingComplete}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {markingComplete ? 'Marking Complete...' : 'Mark Review as Complete'}
                </Button>
              )}
              
              {header.review_status === 'Completed' && header.completed_at && (
                <div className="space-y-3">
                  <div className="text-center text-sm text-green-600">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                    Completed on {formatDate(header.completed_at.split('T')[0])}
                  </div>
                  <Button 
                    variant="outline"
                    className="w-full gap-2"
                    onClick={reopenReview}
                    disabled={reopeningReview}
                    data-testid="reopen-caseload-review-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {reopeningReview ? 'Reopening...' : 'Reopen for Editing'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Caseload Metrics Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-500" />
                Caseload Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Total Caseload Size
                  </span>
                  <span className="font-semibold">{caseloadMetrics?.total_caseload_size || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Pathways Enrollments
                  </span>
                  <span className="font-semibold">{caseloadMetrics?.total_pathways_enrollments || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    HELP Institute Enrollments
                  </span>
                  <span className="font-semibold">{caseloadMetrics?.total_help_institute_enrollments || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Participant Metrics Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-green-500" />
                Participant Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-green-500" />
                    Total Active
                  </span>
                  <Badge variant="outline" className="bg-green-100 text-green-700">{participantMetrics?.total_active || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <UserMinus className="w-4 h-4 text-gray-500" />
                    Total Inactive
                  </span>
                  <Badge variant="outline" className="bg-gray-100 text-gray-700">{participantMetrics?.total_inactive || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-500" />
                    Total Pending Start
                  </span>
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-700">{participantMetrics?.total_pending_start || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <UserX className="w-4 h-4 text-red-500" />
                    Exited This Week
                  </span>
                  <Badge variant="outline" className="bg-red-100 text-red-700">{participantMetrics?.total_exited_this_week || 0}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Data State */}
      {!loading && (!weeks.length || !selectedCaseManager) && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              {!weeks.length ? (
                <>
                  <h3 className="text-lg font-medium mb-2">No Caseload Reviews Available</h3>
                  <p>No weekly caseload review snapshots have been generated yet.</p>
                  <p className="text-sm mt-2">Snapshots are automatically generated every Monday at 7:30 AM Eastern.</p>
                  <Button variant="outline" className="mt-4 gap-2" onClick={generateSnapshot} disabled={generatingSnapshot}>
                    <RefreshCw className={`w-4 h-4 ${generatingSnapshot ? 'animate-spin' : ''}`} />
                    Generate Now
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium mb-2">Select Filters</h3>
                  <p>Please select a week, program, and case manager to view the caseload review.</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Caseload Table with Editable Dropdowns */}
      {selectedCaseManager && records.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Participant Caseload
            </CardTitle>
            <CardDescription>
              {records.length} participant{records.length !== 1 ? 's' : ''} in this caseload review.
              Fields with * are required for completion.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-visible">
            {/* Sticky horizontal scrollbar at bottom of viewport */}
            <style>{`
              .caseload-table-wrapper {
                position: relative;
                overflow: visible;
              }
              .caseload-table-scroll::-webkit-scrollbar {
                height: 12px;
                width: 12px;
              }
              .caseload-table-scroll::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 6px;
              }
              .caseload-table-scroll::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 6px;
              }
              .caseload-table-scroll::-webkit-scrollbar-thumb:hover {
                background: #666;
              }
              .sticky-scrollbar-wrapper {
                position: fixed;
                bottom: 0;
                left: 260px;
                right: 0;
                z-index: 100;
                background: linear-gradient(to bottom, #f8f8f8, #e8e8e8);
                border-top: 2px solid #ccc;
                box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
              }
              .sticky-scrollbar-container {
                overflow-x: auto;
                overflow-y: hidden;
                padding: 10px 16px;
                min-height: 32px;
              }
              .sticky-scrollbar-container::-webkit-scrollbar {
                height: 18px;
              }
              .sticky-scrollbar-container::-webkit-scrollbar-track {
                background: #e5e5e5;
                border-radius: 7px;
              }
              .sticky-scrollbar-container::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 7px;
                border: 2px solid #e5e5e5;
              }
              .sticky-scrollbar-container::-webkit-scrollbar-thumb:hover {
                background: #555;
              }
              @media (max-width: 768px) {
                .sticky-scrollbar-wrapper {
                  left: 0;
                }
              }
            `}</style>
            <div className="caseload-table-wrapper" style={{ overflow: 'visible' }}>
              <div 
                className="caseload-table-scroll"
                ref={tableScrollRef}
                style={{
                  overflowX: 'scroll',
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 350px)',
                  WebkitOverflowScrolling: 'touch'
                }}
                onScroll={(e) => {
                  if (stickyScrollRef.current) {
                    stickyScrollRef.current.scrollLeft = e.target.scrollLeft;
                  }
                }}
              >
                <div style={{ minWidth: '2800px', display: 'inline-block' }}>
                  <Table ref={tableRef}>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-20 min-w-[40px]"></TableHead>
                    <TableHead className="min-w-[100px]">State ID</TableHead>
                    {isSuccessCoachingProgram() ? (
                      <TableHead className="min-w-[160px]" data-testid="participant-column-header">Participant</TableHead>
                    ) : (
                      <>
                        <TableHead className="min-w-[100px]">First Name</TableHead>
                        <TableHead className="min-w-[100px]">Last Name</TableHead>
                      </>
                    )}
                    <TableHead className="min-w-[100px]">Actions</TableHead>
                    <TableHead className="min-w-[200px]">Case Summary</TableHead>
                    {isSuccessCoachingProgram() && (
                      <TableHead className="min-w-[120px] text-center">Success Coaching Sessions</TableHead>
                    )}
                    {isSuccessCoachingProgram() && (
                      <TableHead className="min-w-[200px]">Success Coaching Summary</TableHead>
                    )}
                    <TableHead className="min-w-[100px]">Contact Date</TableHead>
                    <TableHead className="min-w-[160px]">Contact Outcome*</TableHead>
                    <TableHead className="min-w-[130px]">Current Status*</TableHead>
                    <TableHead className="min-w-[110px]">Timesheet*</TableHead>
                    <TableHead className="min-w-[110px]">Certificate</TableHead>
                    <TableHead className="min-w-[160px]">Case Mgmt Type*</TableHead>
                    <TableHead className="min-w-[200px]">Exit Outcome*</TableHead>
                    <TableHead className="min-w-[150px]">Program Name</TableHead>
                    <TableHead className="min-w-[110px]">ABAWD*</TableHead>
                    <TableHead className="min-w-[180px]">Cert Period*</TableHead>
                    <TableHead className="min-w-[100px]">Enroll Date</TableHead>
                    <TableHead className="min-w-[100px]">Exit Date</TableHead>
                    <TableHead className="min-w-[150px]">Employment Goal*</TableHead>
                    <TableHead className="min-w-[180px]">Barriers*</TableHead>
                    <TableHead className="min-w-[150px]">Course Status</TableHead>
                    <TableHead className="min-w-[120px]">Employment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record, idx) => (
                    <TableRow key={record.id || idx} data-testid={`caseload-row-${idx}`}>
                      {/* Completion indicator */}
                      <TableCell className="sticky left-0 bg-background">
                        {isRecordComplete(record) ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <div className="flex items-center gap-1" title={`Missing: ${getMissingFields(record).join(', ')}`}>
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          </div>
                        )}
                      </TableCell>
                      
                      {/* State ID Number - Editable if blank */}
                      <TableCell className="font-medium text-xs">
                        {isStateIdMissing(record) ? (
                          <div className="flex items-center gap-1">
                            {editingStateId[record.id] !== undefined ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="text"
                                  value={editingStateId[record.id] || ''}
                                  onChange={(e) => setEditingStateId(prev => ({ ...prev, [record.id]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateStateIdNumber(record.id, record.participant_id, editingStateId[record.id]);
                                    } else if (e.key === 'Escape') {
                                      setEditingStateId(prev => ({ ...prev, [record.id]: undefined }));
                                    }
                                  }}
                                  placeholder="Enter State ID"
                                  className="h-7 text-xs w-24"
                                  autoFocus
                                  disabled={savingStateId[record.id]}
                                  data-testid={`state-id-input-${record.id}`}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => updateStateIdNumber(record.id, record.participant_id, editingStateId[record.id])}
                                  disabled={savingStateId[record.id] || !editingStateId[record.id]?.trim()}
                                  data-testid={`save-state-id-btn-${record.id}`}
                                >
                                  {savingStateId[record.id] ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingStateId(prev => ({ ...prev, [record.id]: '' }))}
                                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer text-xs border border-amber-300 rounded px-2 py-1 bg-amber-50 hover:bg-amber-100 transition-colors"
                                data-testid={`enter-state-id-btn-${record.id}`}
                              >
                                <AlertCircle className="w-3 h-3" />
                                <span>Enter State ID</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <span>{record.state_id_number}</span>
                        )}
                      </TableCell>
                      {isSuccessCoachingProgram() ? (
                        <TableCell className="text-xs">
                          {record.case_id || record.participant_id ? (
                            <Link
                              to={record.case_id ? `/cases/${record.case_id}` : `/participants/${record.participant_id}`}
                              className="font-medium text-primary hover:underline"
                              data-testid={`participant-link-${idx}`}
                            >
                              {`${record.first_name || ''} ${record.last_name || ''}`.trim() || '-'}
                            </Link>
                          ) : (
                            <span>{`${record.first_name || ''} ${record.last_name || ''}`.trim() || '-'}</span>
                          )}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell className="text-xs">{record.first_name || '-'}</TableCell>
                          <TableCell className="text-xs">{record.last_name || '-'}</TableCell>
                        </>
                      )}
                      
                      {/* Actions Column - Phase 3 */}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-btn-${idx}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuLabel className="text-xs text-muted-foreground">Communications</DropdownMenuLabel>
                            <DropdownMenuItem 
                              onClick={() => openActionDialog(record, 'phone')}
                              className="gap-2"
                              data-testid={`make-call-btn-${idx}`}
                            >
                              <Phone className="h-4 w-4 text-purple-500" />
                              Make Call
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => openActionDialog(record, 'sms')}
                              className="gap-2"
                              data-testid={`send-sms-btn-${idx}`}
                            >
                              <MessageSquare className="h-4 w-4 text-green-500" />
                              Send Text
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => openActionDialog(record, 'email')}
                              className="gap-2"
                              data-testid={`send-email-btn-${idx}`}
                            >
                              <Mail className="h-4 w-4 text-blue-500" />
                              Send Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-muted-foreground">Case Notes</DropdownMenuLabel>
                            <DropdownMenuItem 
                              onClick={() => openActionDialog(record, 'case_note')}
                              className="gap-2"
                              data-testid={`add-note-btn-${idx}`}
                            >
                              <Plus className="h-4 w-4 text-orange-500" />
                              Add Case Note
                            </DropdownMenuItem>
                            {(canFlag || flagsByRecord[record.id]) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs text-muted-foreground">Review</DropdownMenuLabel>
                                {canFlag && (!flagsByRecord[record.id] || flagsByRecord[record.id].status === 'Resolved') && (
                                  <DropdownMenuItem onClick={() => setFlagRecord(record)} className="gap-2" data-testid={`flag-correction-btn-${idx}`}>
                                    <Flag className="h-4 w-4 text-red-500" />
                                    Flag for Correction
                                  </DropdownMenuItem>
                                )}
                                {flagsByRecord[record.id] && (
                                  <DropdownMenuItem onClick={() => navigate(ticketPath(flagsByRecord[record.id].id))} className="gap-2" data-testid={`view-flag-btn-${idx}`}>
                                    <Flag className="h-4 w-4 text-orange-500" />
                                    Open Ticket
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {/* Success Coaching Actions - Only for eligible SNAP E&T enrollments */}
                            {isSuccessCoachingEligible(record) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs text-green-600">Success Coaching</DropdownMenuLabel>
                                <DropdownMenuItem 
                                  onClick={() => openSuccessCoachingAction(record, 'phone')}
                                  className="gap-2"
                                  data-testid={`sc-call-btn-${idx}`}
                                >
                                  <Phone className="h-4 w-4 text-green-500" />
                                  SC: Make Call
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => openSuccessCoachingAction(record, 'sms')}
                                  className="gap-2"
                                  data-testid={`sc-sms-btn-${idx}`}
                                >
                                  <MessageSquare className="h-4 w-4 text-green-500" />
                                  SC: Send Text
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => openSuccessCoachingAction(record, 'email')}
                                  className="gap-2"
                                  data-testid={`sc-email-btn-${idx}`}
                                >
                                  <Mail className="h-4 w-4 text-green-500" />
                                  SC: Send Email
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => openSuccessCoachingAction(record, 'case_note')}
                                  className="gap-2"
                                  data-testid={`sc-note-btn-${idx}`}
                                >
                                  <Plus className="h-4 w-4 text-green-500" />
                                  SC: Add Case Note
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {flagsByRecord[record.id] && flagsByRecord[record.id].status !== 'Resolved' && (
                          <button type="button" className="block" onClick={() => navigate(ticketPath(flagsByRecord[record.id].id))} data-testid={`flag-status-badge-${idx}`}>
                            <Badge variant="outline" className={`mt-1 text-[10px] gap-1 max-w-[110px] text-left ${FLAG_STATUS_STYLES[flagsByRecord[record.id].status]}`}>
                              <Flag className="h-3 w-3" />{flagsByRecord[record.id].status}
                            </Badge>
                          </button>
                        )}
                        {record.last_communication_date && (
                          <div className="text-[10px] text-muted-foreground mt-1">
                            Last: {formatDate(record.last_communication_date.split('T')[0])}
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Case Summary Column - Phase 3 */}
                      <TableCell>
                        {record.case_management_summary ? (
                          <div className="space-y-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-auto p-1 text-left w-full justify-start"
                              onClick={() => viewSummary(record)}
                              data-testid={`view-summary-btn-${idx}`}
                            >
                              <div className="flex items-start gap-1.5">
                                <Sparkles className="h-3 w-3 text-purple-500 mt-0.5 flex-shrink-0" />
                                <span className="text-xs line-clamp-2 text-primary">
                                  {record.case_management_summary.substring(0, 60)}...
                                </span>
                              </div>
                            </Button>
                            <div className="text-[10px] text-muted-foreground pl-1">
                              {record.case_management_summary_note_subject}
                            </div>
                          </div>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 text-xs gap-1"
                            onClick={() => openActionDialog(record, 'case_note')}
                            data-testid={`add-summary-btn-${idx}`}
                          >
                            <Plus className="h-3 w-3" />
                            Add Note
                          </Button>
                        )}
                      </TableCell>
                      
                      {/* Success Coaching Sessions Column - Only for SNAP E&T */}
                      {isSuccessCoachingProgram() && (
                        <TableCell className="text-center">
                          {!isSuccessCoachingEligible(record) ? (
                            <div className="text-xs text-muted-foreground/40 italic">-</div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <span className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-sm font-medium ${
                                (record.success_coaching_sessions_count || 0) === 0 
                                  ? 'bg-gray-100 text-gray-500' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {record.success_coaching_sessions_count || 0}
                              </span>
                            </div>
                          )}
                        </TableCell>
                      )}
                      
                      {/* Success Coaching Summary Column - Only for SNAP E&T */}
                      {isSuccessCoachingProgram() && (
                        <TableCell>
                          {!isSuccessCoachingEligible(record) ? (
                            // Greyed out for enrollments before Oct 1, 2026
                            <div className="text-xs text-muted-foreground/40 italic">
                              Not eligible
                              <div className="text-[10px]">(Pre-Oct 2026)</div>
                            </div>
                          ) : record.success_coaching_summary ? (
                            <div className="space-y-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-auto p-1 text-left w-full justify-start"
                                onClick={() => viewSuccessCoachingSummary(record)}
                                data-testid={`view-success-coaching-btn-${idx}`}
                              >
                                <div className="flex items-start gap-1.5">
                                  <Sparkles className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs line-clamp-2 text-primary">
                                    {record.success_coaching_summary.substring(0, 60)}...
                                  </span>
                                </div>
                              </Button>
                              <div className="text-[10px] text-muted-foreground pl-1">
                                {record.success_coaching_summary_note_subject}
                              </div>
                            </div>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs gap-1 border-green-200 hover:border-green-400"
                              onClick={() => openSuccessCoachingAction(record, 'case_note')}
                              data-testid={`add-success-coaching-btn-${idx}`}
                            >
                              <Plus className="h-3 w-3 text-green-600" />
                              Add Note
                            </Button>
                          )}
                        </TableCell>
                      )}
                      
                      {/* Contact Date - Auto-populated from AI Case Management Summary generation */}
                      <TableCell className="text-xs text-center">
                        {record.case_management_summary_generated_at ? (
                          <span className="text-muted-foreground">
                            {new Date(record.case_management_summary_generated_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </TableCell>
                      
                      {/* Editable dropdowns - Hidden for Exited participants */}
                      {record.current_status === 'Exited' ? (
                        <>
                          {/* Contact Outcome - Read-only for Exited */}
                          <TableCell className="text-xs text-center text-muted-foreground">
                            {record.contact_outcome || '—'}
                          </TableCell>
                          {/* Current Status - Show Exited badge */}
                          <TableCell>
                            <Badge className="bg-gray-100 text-gray-600 border-gray-300 text-xs">
                              Exited
                            </Badge>
                          </TableCell>
                          {/* Timesheet - Read-only for Exited */}
                          <TableCell className="text-xs text-center text-muted-foreground">
                            {record.timesheet || '—'}
                          </TableCell>
                          {/* Certificate - Read-only for Exited */}
                          <TableCell className="text-xs text-center text-muted-foreground">
                            {record.certificate_upload || '—'}
                          </TableCell>
                          {/* Case Mgmt Type - Read-only for Exited */}
                          <TableCell className="text-xs text-center text-muted-foreground">
                            {record.case_management_type || '—'}
                          </TableCell>
                          {/* Exit Outcome - Show value for Exited */}
                          <TableCell>
                            {record.exit_outcome ? (
                              <Badge variant="outline" className="text-xs">
                                {record.exit_outcome}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <InlineSelect
                              value={record.contact_outcome}
                              options={dropdownOptions.contact_outcomes}
                              onChange={(val) => updateRecordField(record.id, 'contact_outcome', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                          <TableCell>
                            <InlineSelect
                              value={record.current_status}
                              options={dropdownOptions.current_statuses}
                              onChange={(val) => updateRecordField(record.id, 'current_status', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                          <TableCell>
                            <InlineSelect
                              value={record.timesheet}
                              options={dropdownOptions.timesheet_options}
                              onChange={(val) => updateRecordField(record.id, 'timesheet', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                          <TableCell>
                            <InlineSelect
                              value={record.certificate_upload}
                              options={dropdownOptions.certificate_upload_options}
                              onChange={(val) => updateRecordField(record.id, 'certificate_upload', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                          <TableCell>
                            <InlineSelect
                              value={record.case_management_type}
                              options={dropdownOptions.case_management_types}
                              onChange={(val) => updateRecordField(record.id, 'case_management_type', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                          <TableCell>
                            <InlineSelect
                              value={record.exit_outcome}
                              options={dropdownOptions.exit_outcomes}
                              onChange={(val) => updateRecordField(record.id, 'exit_outcome', val)}
                              placeholder="Select..."
                              recordId={record.id}
                              disabled={header?.review_status === 'Completed'}
                            />
                          </TableCell>
                        </>
                      )}
                      
                      {/* More read-only fields */}
                      <TableCell className="text-xs">{record.program_name || '-'}</TableCell>
                      
                      {/* ABAWD Status - Editable if missing */}
                      <TableCell>
                        {isAbawdMissing(record) ? (
                          <div className="flex items-center gap-1">
                            {editingTargetedOutcomes[`${record.id}-abawd_status`] !== undefined ? (
                              <div className="flex items-center gap-1">
                                <Select
                                  value={editingTargetedOutcomes[`${record.id}-abawd_status`] || ''}
                                  onValueChange={(value) => {
                                    setEditingTargetedOutcomes(prev => ({ ...prev, [`${record.id}-abawd_status`]: value }));
                                    updateTargetedOutcomesField(record.id, 'abawd_status', value);
                                  }}
                                  disabled={savingTargetedOutcomes[`${record.id}-abawd_status`]}
                                >
                                  <SelectTrigger className="h-7 text-xs w-[90px]">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(dropdownOptions.abawd_statuses || ['ABAWD', 'Non-ABAWD']).map(opt => (
                                      <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {savingTargetedOutcomes[`${record.id}-abawd_status`] && (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingTargetedOutcomes(prev => ({ ...prev, [`${record.id}-abawd_status`]: '' }))}
                                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer text-xs border border-amber-300 rounded px-2 py-1 bg-amber-50 hover:bg-amber-100 transition-colors"
                              >
                                <AlertCircle className="w-3 h-3" />
                                <span>Set ABAWD</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className={
                            record.abawd_status?.toLowerCase() === 'yes' || record.abawd_status?.toLowerCase() === 'abawd' 
                              ? 'bg-orange-100 text-orange-700 border-orange-300 text-xs' 
                              : 'text-xs'
                          }>
                            {record.abawd_status || '-'}
                          </Badge>
                        )}
                      </TableCell>
                      
                      {/* Certification Period - Editable if missing */}
                      <TableCell>
                        {isCertPeriodMissing(record) ? (
                          <div className="flex items-center gap-1">
                            {editingTargetedOutcomes[`${record.id}-certification`] !== undefined ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="date"
                                  value={editingTargetedOutcomes[`${record.id}-certification_start`] || ''}
                                  onChange={(e) => setEditingTargetedOutcomes(prev => ({ 
                                    ...prev, 
                                    [`${record.id}-certification_start`]: e.target.value 
                                  }))}
                                  className="h-7 text-xs w-[110px]"
                                  placeholder="Start"
                                  disabled={savingTargetedOutcomes[`${record.id}-certification_start_date`]}
                                />
                                <span className="text-xs">to</span>
                                <Input
                                  type="date"
                                  value={editingTargetedOutcomes[`${record.id}-certification_end`] || ''}
                                  onChange={(e) => setEditingTargetedOutcomes(prev => ({ 
                                    ...prev, 
                                    [`${record.id}-certification_end`]: e.target.value 
                                  }))}
                                  className="h-7 text-xs w-[110px]"
                                  placeholder="End"
                                  disabled={savingTargetedOutcomes[`${record.id}-certification_end_date`]}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={async () => {
                                    const startDate = editingTargetedOutcomes[`${record.id}-certification_start`];
                                    const endDate = editingTargetedOutcomes[`${record.id}-certification_end`];
                                    if (startDate) {
                                      await updateTargetedOutcomesField(record.id, 'certification_start_date', startDate);
                                    }
                                    if (endDate) {
                                      await updateTargetedOutcomesField(record.id, 'certification_end_date', endDate);
                                    }
                                    setEditingTargetedOutcomes(prev => {
                                      const newState = { ...prev };
                                      delete newState[`${record.id}-certification`];
                                      delete newState[`${record.id}-certification_start`];
                                      delete newState[`${record.id}-certification_end`];
                                      return newState;
                                    });
                                  }}
                                  disabled={
                                    savingTargetedOutcomes[`${record.id}-certification_start_date`] ||
                                    savingTargetedOutcomes[`${record.id}-certification_end_date`] ||
                                    (!editingTargetedOutcomes[`${record.id}-certification_start`] && !editingTargetedOutcomes[`${record.id}-certification_end`])
                                  }
                                >
                                  {(savingTargetedOutcomes[`${record.id}-certification_start_date`] || savingTargetedOutcomes[`${record.id}-certification_end_date`]) ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingTargetedOutcomes(prev => ({ ...prev, [`${record.id}-certification`]: true }))}
                                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer text-xs border border-amber-300 rounded px-2 py-1 bg-amber-50 hover:bg-amber-100 transition-colors"
                              >
                                <AlertCircle className="w-3 h-3" />
                                <span>Set Cert Period</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs">{record.certification_period || '-'}</span>
                        )}
                      </TableCell>
                      
                      <TableCell className="text-xs">{formatDate(record.enrollment_start_date)}</TableCell>
                      <TableCell className="text-xs">{formatDate(record.enrollment_end_date)}</TableCell>
                      
                      {/* Employment Goal - Editable if missing */}
                      <TableCell>
                        {isEmploymentGoalMissing(record) ? (
                          <div className="flex items-center gap-1">
                            {editingTargetedOutcomes[`${record.id}-employment_goal`] !== undefined ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="text"
                                  value={editingTargetedOutcomes[`${record.id}-employment_goal`] || ''}
                                  onChange={(e) => setEditingTargetedOutcomes(prev => ({ 
                                    ...prev, 
                                    [`${record.id}-employment_goal`]: e.target.value 
                                  }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateTargetedOutcomesField(record.id, 'employment_goal', editingTargetedOutcomes[`${record.id}-employment_goal`]);
                                    } else if (e.key === 'Escape') {
                                      setEditingTargetedOutcomes(prev => {
                                        const newState = { ...prev };
                                        delete newState[`${record.id}-employment_goal`];
                                        return newState;
                                      });
                                    }
                                  }}
                                  placeholder="e.g., Full-time employment"
                                  className="h-7 text-xs w-[130px]"
                                  autoFocus
                                  disabled={savingTargetedOutcomes[`${record.id}-employment_goal`]}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => updateTargetedOutcomesField(record.id, 'employment_goal', editingTargetedOutcomes[`${record.id}-employment_goal`])}
                                  disabled={savingTargetedOutcomes[`${record.id}-employment_goal`] || !editingTargetedOutcomes[`${record.id}-employment_goal`]?.trim()}
                                >
                                  {savingTargetedOutcomes[`${record.id}-employment_goal`] ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingTargetedOutcomes(prev => ({ ...prev, [`${record.id}-employment_goal`]: '' }))}
                                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer text-xs border border-amber-300 rounded px-2 py-1 bg-amber-50 hover:bg-amber-100 transition-colors"
                              >
                                <AlertCircle className="w-3 h-3" />
                                <span>Set Goal</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs">{record.employment_goal || '-'}</span>
                        )}
                      </TableCell>
                      
                      {/* Barriers - Editable if missing */}
                      <TableCell>
                        {isBarriersMissing(record) ? (
                          <div className="flex items-center gap-1">
                            {editingTargetedOutcomes[`${record.id}-barriers`] !== undefined ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="text"
                                  value={editingTargetedOutcomes[`${record.id}-barriers`] || ''}
                                  onChange={(e) => setEditingTargetedOutcomes(prev => ({ 
                                    ...prev, 
                                    [`${record.id}-barriers`]: e.target.value 
                                  }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateTargetedOutcomesField(record.id, 'barriers', editingTargetedOutcomes[`${record.id}-barriers`]);
                                    } else if (e.key === 'Escape') {
                                      setEditingTargetedOutcomes(prev => {
                                        const newState = { ...prev };
                                        delete newState[`${record.id}-barriers`];
                                        return newState;
                                      });
                                    }
                                  }}
                                  placeholder="e.g., Transportation, Childcare"
                                  className="h-7 text-xs w-[140px]"
                                  autoFocus
                                  disabled={savingTargetedOutcomes[`${record.id}-barriers`]}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={() => updateTargetedOutcomesField(record.id, 'barriers', editingTargetedOutcomes[`${record.id}-barriers`])}
                                  disabled={savingTargetedOutcomes[`${record.id}-barriers`]}
                                  title="Enter 'None' if no barriers"
                                >
                                  {savingTargetedOutcomes[`${record.id}-barriers`] ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingTargetedOutcomes(prev => ({ ...prev, [`${record.id}-barriers`]: '' }))}
                                className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer text-xs border border-amber-300 rounded px-2 py-1 bg-amber-50 hover:bg-amber-100 transition-colors"
                              >
                                <AlertCircle className="w-3 h-3" />
                                <span>Set Barriers</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs max-w-[150px] truncate" title={formatBarriers(record.barriers)}>
                            {formatBarriers(record.barriers)}
                          </span>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Badge variant="outline" className={
                          record.course_status === 'Course Completed' 
                            ? 'bg-green-100 text-green-700 border-green-300 text-xs'
                            : 'bg-blue-100 text-blue-700 border-blue-300 text-xs'
                        }>
                          {record.course_status || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          record.employment_status === 'Employed' 
                            ? 'bg-green-100 text-green-700 border-green-300 text-xs'
                            : 'bg-gray-100 text-gray-700 border-gray-300 text-xs'
                        }>
                          {record.employment_status || 'Unemployed'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State for selected filters but no records */}
      {selectedCaseManager && records.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Participants</h3>
              <p>No participants found for the selected case manager in this review period.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading caseload review data...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Action Dialog (SMS, Email, Phone, Case Note) */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {getActionDialogIcon()}
              {getActionDialogTitle()}
            </DialogTitle>
            <DialogDescription>
              {selectedRecord && (
                <span>
                  Participant: <strong>{selectedRecord.first_name} {selectedRecord.last_name}</strong>
                  {participantInfo?.participant?.phone && actionType === 'sms' && (
                    <span className="ml-2 text-muted-foreground">({participantInfo.participant.phone})</span>
                  )}
                  {participantInfo?.participant?.email && actionType === 'email' && (
                    <span className="ml-2 text-muted-foreground">({participantInfo.participant.email})</span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingParticipantInfo ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-6 px-6" data-testid="dialog-scroll-body">
              {/* SMS Form */}
              {actionType === 'sms' && (
                <>
                  {!participantInfo?.participant?.phone ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p>This participant does not have a phone number on file.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          placeholder="Enter your text message..."
                          value={smsMessage}
                          onChange={(e) => setSmsMessage(e.target.value)}
                          rows={4}
                          data-testid="sms-message-input"
                        />
                        <p className="text-xs text-muted-foreground text-right">
                          {smsMessage.length} characters ({Math.ceil(smsMessage.length / 160) || 1} segment{Math.ceil(smsMessage.length / 160) !== 1 ? 's' : ''})
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Email Form */}
              {actionType === 'email' && (
                <>
                  {!participantInfo?.participant?.email ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p>This participant does not have an email address on file.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Subject</Label>
                        <Input
                          placeholder="Enter email subject..."
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          data-testid="email-subject-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          placeholder="Enter your email message..."
                          value={emailBody}
                          onChange={(e) => setEmailBody(e.target.value)}
                          rows={6}
                          data-testid="email-body-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Phone Call Form - RingOut */}
              {actionType === 'phone' && (
                <>
                  {!participantInfo?.participant?.phone ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                      <p>This participant does not have a phone number on file.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <Phone className="w-4 h-4 text-blue-500 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-blue-700">RingOut Call</p>
                            <p className="text-blue-600 text-xs">
                              Your phone will ring first, then we'll connect you to the participant.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium">Calling: {participantInfo.participant.phone}</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedRecord.first_name} {selectedRecord.last_name}
                        </p>
                      </div>
                      {outboundNumbers.length > 0 && (
                        <div className="space-y-2">
                          <Label>Caller ID</Label>
                          <Select value={phoneCallerId} onValueChange={setPhoneCallerId}>
                            <SelectTrigger data-testid="phone-caller-id-select">
                              <SelectValue placeholder="Select caller ID" />
                            </SelectTrigger>
                            <SelectContent>
                              {outboundNumbers.map(num => (
                                <SelectItem key={num.phone_number} value={num.phone_number}>
                                  {num.label || num.phone_number} ({num.phone_number})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Call Notes (Optional)</Label>
                        <Textarea
                          placeholder="Pre-call notes or talking points..."
                          value={phoneNotes}
                          onChange={(e) => setPhoneNotes(e.target.value)}
                          rows={3}
                          data-testid="phone-notes-input"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Case Note Form */}
              {actionType === 'case_note' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-purple-700">AI Summary Generation</p>
                        <p className="text-purple-600 text-xs">
                          After saving, AI will generate a Case Management Summary from your note.
                        </p>
                      </div>
                    </div>
                  </div>
                  {successCoachingActionType && (
                    <SuccessCoachingNoteControls
                      caseId={participantInfo?.case?.id || selectedRecord?.case_id}
                      weekInfo={scWeekInfo}
                      unsuccessful={scUnsuccessful}
                      onToggleUnsuccessful={handleScToggleUnsuccessful}
                      answered={scAnswered}
                      onQuestionsComplete={handleScQuestionsComplete}
                    />
                  )}
                  {caseNoteTemplates.length > 0 && (
                    <div className="space-y-2">
                      <Label>Use Template (Optional)</Label>
                      <Select value={selectedTemplate || '_none'} onValueChange={(v) => handleTemplateSelect(v === '_none' ? '' : v)}>
                        <SelectTrigger data-testid="note-template-select">
                          <SelectValue placeholder="Select a template..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">No Template</SelectItem>
                          {getFilteredTemplates().map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Subject *</Label>
                    <Input
                      placeholder="Enter note subject..."
                      value={noteSubject}
                      onChange={(e) => setNoteSubject(e.target.value)}
                      data-testid="note-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date of Service</Label>
                    <Input
                      type="date"
                      value={noteDateOfService}
                      onChange={(e) => setNoteDateOfService(e.target.value)}
                      data-testid="note-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Note Content *</Label>
                    <Textarea
                      placeholder="Enter your case note content..."
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      rows={10}
                      className="min-h-[220px]"
                      data-testid="note-content-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={closeActionDialog} disabled={sendingAction || initiatingCall}>
              Cancel
            </Button>
            {actionType === 'sms' && participantInfo?.participant?.phone && (
              <Button onClick={handleSendSms} disabled={sendingAction || !smsMessage.trim()} className="gap-2">
                {sendingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Text
              </Button>
            )}
            {actionType === 'email' && participantInfo?.participant?.email && (
              <Button onClick={handleSendEmail} disabled={sendingAction || !emailSubject.trim() || !emailBody.trim()} className="gap-2">
                {sendingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send Email
              </Button>
            )}
            {actionType === 'phone' && participantInfo?.participant?.phone && (
              <Button onClick={handleInitiateCall} disabled={initiatingCall || sendingAction} className="gap-2 bg-green-600 hover:bg-green-700">
                {initiatingCall ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                Start Call
              </Button>
            )}
            {actionType === 'case_note' && !successCoachingActionType && (
              <Button onClick={handleAddCaseNote} disabled={sendingAction || !noteSubject.trim() || !noteContent.trim()} className="gap-2">
                {sendingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Save & Generate Summary
              </Button>
            )}
            {actionType === 'case_note' && successCoachingActionType && (
              <Button 
                onClick={handleAddSuccessCoachingCaseNote} 
                disabled={sendingAction || !noteSubject.trim() || !noteContent.trim()} 
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {sendingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Save SC Note & Generate Summary
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3: Case Management Summary View Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Case Management Summary
            </DialogTitle>
            <DialogDescription>
              {viewingSummaryRecord && (
                <span>
                  Participant: <strong>{viewingSummaryRecord.first_name} {viewingSummaryRecord.last_name}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-6 px-6" data-testid="dialog-scroll-body">
            {viewingSummaryRecord?.case_management_summary_note_subject && (
              <div>
                <Label className="text-muted-foreground text-xs">Based on Case Note</Label>
                <p className="font-medium">{viewingSummaryRecord.case_management_summary_note_subject}</p>
              </div>
            )}
            
            <ManagedSummaryPanel
              kind="case_management"
              record={viewingSummaryRecord}
              emptyText="No summary available"
              boxClassName="bg-gray-50 rounded-lg p-4"
              canManage={canManageSummary(viewingSummaryRecord)}
              onUpdated={(patch) => applySummaryUpdate(viewingSummaryRecord.id, patch)}
            />
            
            {viewingSummaryRecord?.case_management_summary_generated_at && (
              <p className="text-xs text-muted-foreground">
                Generated: {formatDate(viewingSummaryRecord.case_management_summary_generated_at.split('T')[0])}
              </p>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setSummaryDialogOpen(false);
              openActionDialog(viewingSummaryRecord, 'case_note');
            }} className="gap-2">
              <Plus className="h-4 w-4" />
              Add New Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Coaching Summary View Dialog */}
      <Dialog open={successCoachingSummaryDialogOpen} onOpenChange={setSuccessCoachingSummaryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-500" />
              AI Success Coaching Summary
            </DialogTitle>
            <DialogDescription>
              {viewingSuccessCoachingRecord && (
                <span>
                  Participant: <strong>{viewingSuccessCoachingRecord.first_name} {viewingSuccessCoachingRecord.last_name}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-6 px-6" data-testid="dialog-scroll-body">
            {viewingSuccessCoachingRecord?.success_coaching_summary_note_subject && (
              <div>
                <Label className="text-muted-foreground text-xs">Based on Case Note</Label>
                <p className="font-medium">{viewingSuccessCoachingRecord.success_coaching_summary_note_subject}</p>
              </div>
            )}
            
            <ManagedSummaryPanel
              kind="success_coaching"
              record={viewingSuccessCoachingRecord}
              emptyText="No Success Coaching summary available yet. A summary will be generated when a case note is logged from a Success Coaching template."
              boxClassName="bg-green-50 rounded-lg p-4 border border-green-100"
              canManage={canManageSummary(viewingSuccessCoachingRecord)}
              onUpdated={(patch) => applySummaryUpdate(viewingSuccessCoachingRecord.id, patch)}
            />
            
            {viewingSuccessCoachingRecord?.success_coaching_summary_generated_at && (
              <p className="text-xs text-muted-foreground">
                Generated: {formatDate(viewingSuccessCoachingRecord.success_coaching_summary_generated_at.split('T')[0])}
              </p>
            )}
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setSuccessCoachingSummaryDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setSuccessCoachingSummaryDialogOpen(false);
                openSuccessCoachingAction(viewingSuccessCoachingRecord, 'case_note');
              }} 
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              Add SC Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FlagForCorrectionDialog record={flagRecord} open={!!flagRecord} onOpenChange={(o) => { if (!o) setFlagRecord(null); }} />
    </div>
  );
};

export default CaseloadReviewPage;
